/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, onConnect } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'

import type {
  TradeOrdersListData,
  TradeOrderTokensListData,
} from '@blade/trade-api-client'

export type Order = TradeOrdersListData[number]
export type OrderToken = TradeOrderTokensListData[number]

/**
 * What is waiting on a price, and on which tokens.
 *
 * Two reads rather than one: the left panel is a way into the list, so it
 * counts per token on the server instead of the screen counting a page it
 * has not finished loading.
 */
export const fetchOrdersRequestAction = action(
  async (
    ctx,
    query: { network: ReturnType<typeof Network.toDto>; tokenAddress?: string },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeOrdersList, [
        {
          network: query.network,
          ...(query.tokenAddress ? { tokenAddress: query.tokenAddress } : {}),
        },
      ]),
    )

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'fetchOrdersRequestAction',
)

export const fetchOrderTokensRequestAction = action(
  async (ctx, query: { network: ReturnType<typeof Network.toDto> }) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeOrderTokensList, [query]),
    )

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'fetchOrderTokensRequestAction',
)

export const reatomOrders = () => {
  const selectedTokenAtom = atom<string | undefined>(
    undefined,
    'selectedTokenAtom',
  )

  const ordersState = reatomRequest({
    name: 'ordersState',
    execAction: fetchOrdersRequestAction,
  })

  const tokensState = reatomRequest({
    name: 'orderTokensState',
    execAction: fetchOrderTokensRequestAction,
  })

  /** Read both again: cancelling changes the list and the counts beside it. */
  const refreshAction = action((ctx) => {
    const network = Network.toDto(ctx.get(selectedNetworkAtom))
    const tokenAddress = ctx.get(selectedTokenAtom)

    ordersState.execute(ctx, {
      network,
      ...(tokenAddress ? { tokenAddress } : {}),
    })
    tokensState.execute(ctx, { network })
  }, 'refreshOrdersAction')

  const selectTokenAction = action((ctx, tokenAddress: string | undefined) => {
    selectedTokenAtom(ctx, tokenAddress)
    refreshAction(ctx)
  }, 'selectTokenAction')

  const cancelAction = action(async (ctx, orderId: string) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      // Positional: the route carries no query parameters.
      apiRequest(ctx, tradeApiClient.trade.tradeOrderCancel, [orderId]),
    )

    if (Result.isSuccess(result)) {
      refreshAction(ctx)
    }
    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  }, 'cancelOrderAction')

  const cancelAllAction = action(async (ctx) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const network = Network.toDto(ctx.get(selectedNetworkAtom))
    const tokenAddress = ctx.get(selectedTokenAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeOrdersCancelAll, [
        {
          network,
          ...(tokenAddress ? { tokenAddresses: [tokenAddress] } : {}),
        },
      ]),
    )

    if (Result.isSuccess(result)) {
      refreshAction(ctx)
    }
    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  }, 'cancelAllOrdersAction')

  onConnect(ordersState.requestStateAtom, refreshAction)

  return {
    selectedTokenAtom,
    ordersState: ordersState.requestStateAtom,
    tokensState: tokensState.requestStateAtom,
    selectTokenAction,
    refreshAction,
    cancelAction,
    cancelAllAction,
  }
}

export type OrdersModel = ReturnType<typeof reatomOrders>
